import { useEffect, useRef, useState } from "react";
import { collision, kindWord, nextFree, type LabelOwner } from "@/lib/labels/labelKeys";
import { testIds } from "@/testIds";

/**
 * A block's label, shown as a small monospace chip beside its caption.
 *
 * Not a field. Left to a field, authors write `tab1` or nothing, and nothing is
 * why referencing was cumbersome. So when the block has no label yet the chip
 * shows the one its caption *would* give it — *derived from the caption · click
 * to change* — and the parent writes that on save. Clicking edits it.
 * (Olivia, SPEC-references §4.)
 *
 * A collision warns and offers the next free key. It never blocks: LaTeX
 * silently keeping the last of two identical labels is the bug, and refusing to
 * save would be a worse one.
 */
export function LabelChip({
  value,
  derived,
  others,
  onChange,
}: {
  /** The label the block has. Empty when it has none yet. */
  value: string;
  /** What the caption would give it, or null when there is nothing to derive. */
  derived: string | null;
  /** Every other label in the document — not this block's own. */
  others: readonly LabelOwner[];
  onChange: (next: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing) input.current?.select();
  }, [editing]);

  const shown = value || derived || "";
  const isDerived = !value && !!derived;
  const clash = value ? collision(value, others) : null;

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next !== value) onChange(next);
  };

  return (
    <div className="flex flex-col gap-1">
      {editing ? (
        <input
          ref={input}
          data-testid={testIds.labels.chipEdit}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit();
            } else if (e.key === "Escape") {
              e.preventDefault();
              setDraft(value);
              setEditing(false);
            }
          }}
          placeholder={derived ?? "tab:results"}
          spellCheck={false}
          className="w-full rounded-[var(--lilia-radius-nested,6px)] px-2 py-1 font-mono text-[12px] outline-none"
          style={{ background: "var(--lilia-bg)", border: "1px solid var(--lilia-accent, var(--lilia-primary))" }}
        />
      ) : (
        <button
          type="button"
          data-testid={testIds.labels.chip}
          data-derived={isDerived ? "true" : "false"}
          data-label={shown}
          onClick={() => {
            // Editing a derived key starts from the derived key, not from empty.
            setDraft(value || derived || "");
            setEditing(true);
          }}
          title={isDerived ? "Derived from the caption · click to change" : "Click to change"}
          className="inline-flex max-w-full items-center gap-1.5 self-start truncate rounded-[var(--lilia-radius-nested,6px)] px-2 py-[3px] font-mono text-[12px]"
          style={{
            background: "var(--lilia-bg-secondary, var(--lilia-surface-alt))",
            color: isDerived ? "var(--lilia-text-secondary)" : "var(--lilia-text)",
            border: clash ? "1px solid var(--lilia-warning, #b5830e)" : "1px solid transparent",
          }}
        >
          {shown || <span className="font-sans italic">no label — click to add</span>}
        </button>
      )}

      {isDerived && !editing && (
        <span className="text-[11px] text-[color:var(--lilia-text-secondary)]">
          derived from the caption · click to change
        </span>
      )}

      {clash && !editing && (
        <div
          data-testid={testIds.labels.collision}
          className="flex flex-wrap items-center gap-2 text-[11.5px]"
          style={{ color: "var(--lilia-warning, #b5830e)" }}
        >
          <span>Already used by {kindWord(clash.kind)} — LaTeX will keep only the last.</span>
          <button
            type="button"
            data-testid={testIds.labels.collisionAccept}
            onClick={() => onChange(nextFree(value, new Set(others.map((o) => o.key))))}
            className="rounded-[var(--lilia-radius-nested,6px)] px-2 py-[2px] font-mono text-[11.5px] font-semibold"
            style={{ background: "var(--lilia-warning-subtle, var(--lilia-surface-alt))" }}
          >
            Use {nextFree(value, new Set(others.map((o) => o.key)))}
          </button>
        </div>
      )}
    </div>
  );
}
